import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';

import {
  Card,
  DataTable,
  DataTableColumn,
  DataTableCell,
  MediaObject,
  Spinner
} from '@salesforce/design-system-react';

import './ImportList.scss';

const CustomDataTableCell = ({ children, property, ...props }) => {
  const badgeFor = status => {
    switch (status.statusIcon) {
      case 'importing':
        return 'slds-theme_inverse';
      case 'complete':
        return 'slds-theme_success';
      case 'error':
        return 'slds-theme_error';
      case 'complete_with_error':
        return 'slds-theme_warning';
      default:
        return '';
    }
  };
  switch (property) {
    case 'status':
      let onClick = () => {};
      let clickableClass = '';
      if (['error', 'complete_with_error'].includes(props.item.statusIcon)) {
        const clickable =
          props.item.status_errors && props.item.status_errors.length;
        if (clickable) {
          onClick = () => props.onDetailsClick(props.item);
          clickableClass = 'clickable-badge';
        }
      }
      const badge = badgeFor(props.item);
      return (
        <td>
          <span
            className={`profile-badge-cell slds-badge ${badge} ${clickableClass}`}
            onClick={onClick}
          >
            {children}
          </span>
        </td>
      );
    default: {
      return <td className="wrap-text profile-cell">{children}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  onDetailsClick: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object
};

class ImportList extends Component {
  headerCell(property, formatMessage) {
    return formatMessage({ id: `components.ecs.profiles.${property}` });
  }

  columns() {
    return {
      name: { sortable: true },
      status: { sortable: false, width: '250px' },
      syncedProfiles: { sortable: false, width: '250px' },
      lastUpdatedAt: { sortable: true }
    };
  }

  tableData(items) {
    if (!items) {
      return [];
    }

    const { formatMessage, formatNumber } = this.props.intl;
    return items.map(item => {
      const syncedProfiles =
        item.source_info.imported_rows && item.source_info.total_rows
          ? formatMessage(
              {
                id: 'components.ecs.profiles.syncedProfilesCounts'
              },
              {
                imported: formatNumber(item.source_info.imported_rows),
                total: formatNumber(item.source_info.total_rows)
              }
            )
          : '';

      return {
        ...item,
        statusIcon: item.status,
        status: formatMessage({
          id: `components.ecs.profiles.statuses.${item.status}`
        }),
        name: item.source_info.name,
        syncedProfiles,
        lastUpdatedAt: formatTimestamp(this.props.intl)(item.updated_at),
        id: item.import_id
      };
    });
  }

  header(items) {
    const count = items ? items.length : 0;
    return (
      <div className="profile-import-header">
        <h2>
          <FormattedMessage id="components.ecs.profiles.label" />
        </h2>
        <FormattedMessage
          id="components.ecs.catalogs.items"
          values={{ count }}
        />
      </div>
    );
  }

  renderTable(items) {
    const { formatMessage } = this.props.intl;
    return (
      <Card
        className="profile-import-card"
        header={<MediaObject body={this.header(items)} />}
      >
        <DataTable items={items} className="profile-list-table" fixedLayout>
          {Object.entries(this.columns()).map((column, index) => {
            const sortable = column[1].sortable;
            const width = column[1].width;
            const property = column[0];
            return (
              <DataTableColumn key={index} sortable={sortable}>
                <CustomDataTableCell
                  property={property}
                  formatMessage={formatMessage}
                  label={this.headerCell(property, formatMessage)}
                  width={width}
                  onDetailsClick={this.props.onDetailsClick}
                />
              </DataTableColumn>
            );
          })}
        </DataTable>
      </Card>
    );
  }

  drawSpinner(isLoading, hasLoadedOnce) {
    return isLoading && !hasLoadedOnce ? (
      <div className="pi-common-spinner">
        <Spinner variant="brand" />
      </div>
    ) : null;
  }

  render() {
    const { imports } = this.props;
    const canRender = imports.data && imports.data.imports;

    return (
      <React.Fragment>
        {canRender
          ? this.renderTable(this.tableData(imports.data.imports.DeImport))
          : null}
        {this.drawSpinner(imports.isLoading, imports.hasLoadedOnce)}
      </React.Fragment>
    );
  }
}

ImportList.contextTypes = {
  router: PropTypes.object
};

ImportList.propTypes = {
  imports: PropTypes.PropTypes.shape({
    data: PropTypes.shape({
      imports: PropTypes.object.isRequired
    }),
    isLoading: PropTypes.bool.isRequired
  }).isRequired,
  onDetailsClick: PropTypes.func.isRequired,
  actions: PropTypes.object,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(ImportList);
